// Link a collection to a remote board / list ("binding"). With buckets enabled, a Microsoft To Do
// list can become a new bucket or be mapped to an existing one (v0.4 multi-list).
import { useEffect, useMemo, useState } from "react";
import { api, errorMessage } from "../api";
import { BINDING_MODES, type BindingMode, type Bucket, type Connection, type RemoteCollection } from "../api/types";
import { bucketCapability } from "../lib/bucketRemote";
import { connectorLabel } from "../lib/format";
import { loadBuckets, loadCollection, loadConnectors, loadCollections, loadItems, toast, toastError } from "../store/app";
import { useApp } from "../store/hooks";
import { ErrorBox, Modal, Spinner } from "./bits";

type BucketChoice = "new" | "existing";

export function AddBindingDialog({
  collectionId,
  spaceId,
  bucketsEnabled,
  buckets,
  onClose,
}: {
  collectionId: string;
  spaceId: string;
  bucketsEnabled: boolean;
  buckets: Bucket[];
  onClose: () => void;
}) {
  const orgId = useApp((s) => s.orgId);
  const manifests = useApp((s) => s.connectors);
  const [connections, setConnections] = useState<Connection[] | null>(null);
  const [connectionId, setConnectionId] = useState("");
  const [remote, setRemote] = useState<RemoteCollection[] | null>(null);
  const [remoteId, setRemoteId] = useState("");
  const [mode, setMode] = useState<BindingMode>("mirror");
  const [choice, setChoice] = useState<BucketChoice>("new");
  // Only buckets that aren't a list already can be mapped.
  const mappable = useMemo(() => buckets.filter((b) => !b.binding_id), [buckets]);
  const [bucketId, setBucketId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void loadConnectors();
  }, []);

  useEffect(() => {
    if (!orgId) return;
    api
      .connections(orgId)
      .then((r) => {
        setConnections(r.connections);
        setConnectionId((cur) => cur || r.connections[0]?.id || "");
      })
      .catch((e) => setError(errorMessage(e)));
  }, [orgId]);

  useEffect(() => {
    if (!connectionId) return;
    setRemote(null);
    setRemoteId("");
    api
      .remoteCollections(connectionId)
      .then((r) => setRemote(r.collections))
      .catch((e) => setError(errorMessage(e)));
  }, [connectionId]);

  useEffect(() => setBucketId((cur) => cur || mappable[0]?.id || ""), [mappable]);

  const connection = connections?.find((c) => c.id === connectionId);
  const asBucket = bucketsEnabled && !!connection && bucketCapability(connection.connector_type, manifests) === "lists";

  return (
    <Modal title="Link to an external board or list" onClose={onClose} wide>
      {error && <ErrorBox error={error} />}
      {connections === null && !error ? (
        <Spinner />
      ) : connections && connections.length === 0 ? (
        <p className="muted">No connections yet. Add one under Settings → Connections.</p>
      ) : (
        <form
          className="form"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!connectionId || !remoteId) return;
            setBusy(true);
            try {
              await api.createBinding(collectionId, {
                connection_id: connectionId,
                remote_collection_id: remoteId,
                mode,
                ...(asBucket ? (choice === "new" || !bucketId ? { new_bucket: true } : { bucket_id: bucketId }) : {}),
              });
              await Promise.all([
                loadCollection(collectionId),
                loadCollections(spaceId),
                loadItems(collectionId),
                bucketsEnabled ? loadBuckets(collectionId) : undefined,
              ]);
              toast("Linked. The first sync runs in the background.", "success");
              onClose();
            } catch (err) {
              toastError(err, "Could not link");
              setBusy(false);
            }
          }}
        >
          <div className="field-row">
            <label className="field">
              <span>Connection</span>
              <select value={connectionId} onChange={(e) => setConnectionId(e.target.value)}>
                {connections?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {connectorLabel(c.connector_type)} · {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Sync mode</span>
              <select value={mode} onChange={(e) => setMode(e.target.value as BindingMode)}>
                {BINDING_MODES.map((m) => (
                  <option key={m} value={m}>
                    {m.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {remote === null ? (
            <Spinner label="Loading remote boards and lists…" />
          ) : remote.length === 0 ? (
            <p className="muted">Nothing found in this connection.</p>
          ) : (
            <fieldset className="remote-list">
              <legend>Board / list</legend>
              {remote.map((r) => (
                <label key={r.remote_id} className={`remote-row${r.bound_collection_id ? " bound" : ""}`}>
                  <input
                    type="radio"
                    name="remote"
                    checked={remoteId === r.remote_id}
                    disabled={!!r.bound_collection_id}
                    onChange={() => setRemoteId(r.remote_id)}
                  />
                  <span className="remote-name">{r.name}</span>
                  <span className="muted small">{r.kind}</span>
                  {r.bound_collection_id && <span className="badge tiny">already linked</span>}
                </label>
              ))}
            </fieldset>
          )}
          {asBucket && (
            <fieldset className="field radio-col">
              <legend>Bucket</legend>
              <label>
                <input type="radio" name="bucket-choice" checked={choice === "new"} onChange={() => setChoice("new")} />
                Use as new bucket <span className="muted small">(named after the list)</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="bucket-choice"
                  checked={choice === "existing"}
                  disabled={mappable.length === 0}
                  onChange={() => setChoice("existing")}
                />
                Map to existing bucket
                {mappable.length === 0 ? (
                  <span className="muted small">(every bucket is already a list)</span>
                ) : (
                  <select
                    aria-label="Existing bucket"
                    value={bucketId}
                    disabled={choice !== "existing"}
                    onChange={(e) => setBucketId(e.target.value)}
                  >
                    {mappable.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                )}
              </label>
              <p className="muted small">Moving a card into this bucket moves the task into this list.</p>
            </fieldset>
          )}
          <div className="form-actions">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={busy || !remoteId}>
              {busy ? "Linking…" : "Link"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
